import type { Theme } from '@mui/material/styles';
import {
  SentimentDto,
  CallDto,
  SipTrunkResponseDto,
  CampaignSipTrunkCreateRequestDto,
  CampaignType,
} from '@shared/services/apiService/apiService';
import { ActiveFilterEnum, SidebarSubTabFieldsMap } from './constants';
import { ActiveFilterType, RecipientData, SidebarTabNamesType } from './types';

export { getRemainingCallSeconds } from '@features/Admin/Campaign/utils/campaign-usage.utils';
export {
  buildReportQueryParameters,
  getStoredAdditionalColumns,
  saveSelectedAdditionalColumns,
  getInitialAdditionalColumns,
  getToggledAdditionalColumns,
} from '@features/Admin/Campaign/utils/ongoing-calls.utils';

export const formatCallType = (type: string): string =>
  type.replace(/-/g, ' ').replace(/^./, (c) => c.toUpperCase());

export const getCallDuration = (answeredAt: string, terminatedAt?: string) => {
  const answeredAtTime = new Date(answeredAt).getTime();
  const terminatedAtTime = terminatedAt ? new Date(terminatedAt).getTime() : Date.now();

  const durationSec = Math.floor((terminatedAtTime - answeredAtTime) / 1000);
  const hours = String(Math.floor(durationSec / 3600)).padStart(2, '0');
  const minutes = String(Math.floor((durationSec % 3600) / 60)).padStart(2, '0');
  const seconds = String(durationSec % 60).padStart(2, '0');

  return `${hours}:${minutes}:${seconds}`;
};

export const getCurrentCallDuration = (answeredAt: string): string => {
  const answeredAtTime = new Date(answeredAt).getTime();
  const currentTime = Date.now();

  const durationSec = Math.floor((currentTime - answeredAtTime) / 1000);
  const hours = String(Math.floor(durationSec / 3600)).padStart(2, '0');
  const minutes = String(Math.floor((durationSec % 3600) / 60)).padStart(2, '0');
  const seconds = String(durationSec % 60).padStart(2, '0');

  return `${hours}:${minutes}:${seconds}`;
};

export const getMedianCallDurationInMin = (callDuration?: number | null): string => {
  if (!callDuration) {
    return 'N/A';
  }
  const roundedValue = Math.round(callDuration);

  const hours = String(Math.floor(roundedValue / 3600)).padStart(2, '0');
  const minutes = String(Math.floor((roundedValue % 3600) / 60)).padStart(2, '0');
  const secs = String(roundedValue % 60).padStart(2, '0');

  return `${hours}:${minutes}:${secs}`;
};

type FormatCampaignDateTimeOptions = {
  includeSeconds?: boolean;
  hour12?: boolean;
  useDots?: boolean;
  omitComma?: boolean;
};

export const formatCampaignDateTime = (
  value: string,
  options?: FormatCampaignDateTimeOptions,
): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const formatted = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...(options?.includeSeconds ? { second: '2-digit' } : {}),
    ...(options?.hour12 !== undefined ? { hour12: options.hour12 } : {}),
  }).format(date);
  let normalized = formatted;
  if (options?.useDots) {
    normalized = normalized.replace(/\//g, '.');
  }
  if (options?.omitComma) {
    normalized = normalized.replace(',', '');
  }
  return normalized;
};

export function getTotalSentimentsScore(sentimentData: SentimentDto[]): number {
  return sentimentData.reduce((acc, data) => {
    return (
      acc +
      data.sentimentScores.reduce((innerAcc, sentiment) => {
        switch (sentiment.label) {
          case 'positive':
            return innerAcc + sentiment.score;
          case 'neutral':
            return innerAcc + sentiment.score * 0;
          case 'negative':
            return innerAcc - sentiment.score;
          default:
            return innerAcc;
        }
      }, 0)
    );
  }, 0);
}

export function getSentimentsColor(sentimentData: SentimentDto[], theme: Theme): string {
  const totalScore = getTotalSentimentsScore(sentimentData);

  const totalSentimentScoresCount = sentimentData.reduce((count, data) => {
    return count + data.sentimentScores.length;
  }, 0);

  const averageScore = totalSentimentScoresCount > 0 ? totalScore / totalSentimentScoresCount : 0;

  if (averageScore > 0) {
    return theme.palette.success.main;
  }
  if (averageScore === 0) {
    return theme.palette.warning.main;
  }
  return theme.palette.error.main;
}

export const formatInteractionResult = (input: string): string => {
  return input.replace(/[_-]/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};

export const getSidebarTabName = (field: string): SidebarTabNamesType => {
  return SidebarSubTabFieldsMap[field];
};

type CallWithAnalysisStatus = CallDto & { analysisStatus?: RecipientData['analysisStatus'] };

export const CallsToRecipientsMapper = (outboundRecipients: CallDto[]): RecipientData[] => {
  return (outboundRecipients as CallWithAnalysisStatus[])?.map(
    ({
      id,
      status,
      sentiments,
      endedReason,
      endedAt,
      startedAt,
      customer,
      recordingUrl,
      createdAt,
      type,
      analysis,
      agent,
      squadId,
      squad,
      analysisStatus,
      agentProfileName,
    }) => ({
      id,
      mainId: id,
      sentiments: sentiments || [],
      status,
      interactionResult: endedReason ? formatInteractionResult(endedReason) : null,
      endedReason: endedReason || null, // Store original endedReason for filtering
      phoneNumber: customer?.number,
      answeredAt: startedAt,
      createdAt: createdAt,
      endedAt: endedAt,
      startDate: startedAt as string,
      callDuration: startedAt && endedAt ? getCallDuration(startedAt, endedAt) : null,
      recordingUrl: recordingUrl,
      type,
      agentRole: agentProfileName,
      analysis: analysis ?? null,
      analysisStatus: analysisStatus ?? null,
    }),
  ) as RecipientData[];
};

export const mapCampaignSipToDefaultSip = (
  campaignSip: SipTrunkResponseDto,
): CampaignSipTrunkCreateRequestDto => {
  return {
    type: campaignSip.type,
    numbers: campaignSip.numbers,
    address: campaignSip.address || undefined,
    transport: campaignSip.transport,
    allowedAddresses: campaignSip.allowedAddresses || undefined,
    authUsername: undefined,
    authPassword: undefined,
    fromHost: campaignSip.fromHost || undefined,
    dialCountry: campaignSip.dialCountry || undefined,
    dialPrefix: campaignSip.dialPrefix || undefined,
  };
};

const campaignTypeFilters: Record<CampaignType, ActiveFilterType> = {
  inbound: ActiveFilterEnum['INBOUND-PHONE-CALL'],
  outbound: ActiveFilterEnum['OUTBOUND-PHONE-CALL'],
};

const baseFilters: ActiveFilterType[] = [
  ActiveFilterEnum['WEB-CALL'],
  ActiveFilterEnum['WEBSOCKET-CALL'],
  ActiveFilterEnum.ALL,
];

export function getCallsFiltersByCampaignType(campaignType: CampaignType): ActiveFilterType[] {
  return [campaignTypeFilters[campaignType], ...baseFilters];
}
